<template>
  <div class="w-full p-4 cursor-default text-sm">
    <span class="font-medium mr-2 flex items-center">
      <MessageSquareMore class="w-4 h-4 mr-2" />Comments:
    </span>
    
    <!-- Display mode -->
    <div
      v-if="!isEditing"
      @click="startEditing"
      class="mt-2 cursor-pointer hover:bg-gray-50 p-2 rounded transition-colors"
    >
      <span v-if="localComments" class="block">{{ localComments }}</span>
      <span v-else class="text-gray-600 ml-4 opacity-60 text-sm italic">
        Click to add comments...
      </span>
    </div>
    
    <!-- Edit mode -->
    <div v-if="isEditing" class="mt-2">
      <textarea
        ref="textareaRef"
        v-model="editingComments"
        class="w-full text-sm p-2 border border-gray-300 rounded-md resize-none focus:outline-none focus:ring-2 focus:border-transparent"
        rows="3"
        placeholder="Add your comments here..."
        @keydown.escape="cancelEditing"
      />
      <div class="flex gap-2 mt-2 text-xs justify-end">
        <button
          @click="cancelEditing"
          :disabled="isSaving"
          class="px-4 py-2 bg-gray-200 text-gray-700 rounded-md hover:bg-gray-300 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-300"
        >
          Cancel
        </button>
        <button
          @click="saveComments"
          :disabled="isSaving"
          class="px-4 py-2 bg-cosmicPurple text-white shadow-md rounded-md hover:bg-cosmicPurple/80 disabled:opacity-50 disabled:cursor-not-allowed flex items-center transition-all duration-300"
        >
          {{ isSaving ? 'Saving...' : 'Submit' }}
        </button>
  
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, nextTick } from 'vue'
import { MessageSquareMore } from 'lucide-vue-next'
import { useApplicationsStore } from '@/stores/applications'
import { useToast } from '@/components/ui/toast'

interface Props {
  comments?: string
  applicationId: string
  disabled?: boolean
}

const props = withDefaults(defineProps<Props>(), {
  comments: '',
  disabled: false
})

const emit = defineEmits<{
  commentsUpdated: [comments: string]
}>()

const applicationsStore = useApplicationsStore()
const { toast } = useToast()

const isEditing = ref(false)
const isSaving = ref(false)
const localComments = ref(props.comments)
const editingComments = ref('')
const textareaRef = ref<HTMLTextAreaElement>()

const startEditing = () => {
  if (props.disabled) return
  
  isEditing.value = true
  editingComments.value = localComments.value
  
  // eslint-disable-next-line @typescript-eslint/no-floating-promises
  nextTick(() => {
    textareaRef.value?.focus()
  })
}

const cancelEditing = () => {
  isEditing.value = false
  editingComments.value = ''
}

const saveComments = async () => {
  if (isSaving.value) return
  
  isSaving.value = true
  
  try {
    await applicationsStore.updateApplicationComments(props.applicationId, editingComments.value)
    localComments.value = editingComments.value
    emit('commentsUpdated', editingComments.value)
    isEditing.value = false
    
    toast({
      title: 'Success',
      description: 'Comments saved successfully',
    })
  } catch (error) {
    console.error('Failed to save comments:', error)
    toast({
      title: 'Error',
      description: 'Failed to save comments. Please try again.',
      variant: 'destructive',
    })
  } finally {
    isSaving.value = false
  }
}
</script>